import { Chip, type ChipProps } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

/**
 * Monitor status type matching the server-side MonitorStatus enum
 */
export type MonitorStatus = 'UP' | 'DOWN' | 'PENDING' | 'PAUSED';

/**
 * Status configuration with colors and icons
 */
const statusConfig: Record<
  MonitorStatus,
  {
    label: string;
    color: string;
    backgroundColor: string;
    icon: React.ReactElement;
  }
> = {
  UP: {
    label: 'Up',
    color: '#10B981',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
  },
  DOWN: {
    label: 'Down',
    color: '#EF4444',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    icon: <ErrorIcon sx={{ fontSize: 16 }} />,
  },
  PENDING: {
    label: 'Pending',
    color: '#6B7280',
    backgroundColor: 'rgba(107, 114, 128, 0.1)',
    icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
  },
  PAUSED: {
    label: 'Paused',
    color: '#F59E0B',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    icon: <PauseCircleIcon sx={{ fontSize: 16 }} />,
  },
};

/**
 * StatusBadge component props
 */
interface StatusBadgeProps {
  status: MonitorStatus;
  size?: 'small' | 'medium';
  showIcon?: boolean;
  variant?: ChipProps['variant'];
}

/**
 * StatusBadge component
 * Displays a colored badge indicating monitor status
 */
export function StatusBadge({
  status,
  size = 'small',
  showIcon = true,
  variant = 'filled',
}: StatusBadgeProps) {
  const config = statusConfig[status];

  // Build chip props conditionally to avoid undefined icon
  const chipProps = {
    label: config.label,
    size,
    variant,
    ...(showIcon && { icon: config.icon }),
  };

  return (
    <Chip
      {...chipProps}
      sx={{
        color: config.color,
        backgroundColor: config.backgroundColor,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        '& .MuiChip-icon': {
          color: config.color,
        },
        ...(variant === 'outlined' && {
          backgroundColor: 'transparent',
          borderColor: config.color,
        }),
      }}
    />
  );
}

export default StatusBadge;
